import { useQuery } from '@tanstack/react-query';
import envVars from '../../../constants/envVars';
import useRequest from '../../../hooks/useRequest';

// HWM-22633: Polycare / DrChrono medical-check slot picker. Mirrors the
// /scheduling/available-slots/ contract used by hero-storefront-app.

// HWM-23070: `start_utc` / `end_utc` / `clinic_timezone` / `first_available_date`
// are the UTC slot contract. Optional only for the migration window — an env
// running an older rtm-api omits them; the retirement ticket drops the naive
// `time` / `end_time` / `timezone` fields and makes these required.
export interface TimeSlot {
    time: string;
    end_time: string;
    available: boolean;
    start_utc?: string;
    end_utc?: string;
}

export interface AppointmentHours {
    start: string;
    end: string;
    start_utc?: string;
    end_utc?: string;
}

export interface AvailableSlotsData {
    clinician_available_now: boolean;
    provider_name: string;
    appointment_hours: Record<string, AppointmentHours | null>;
    slots: Record<string, TimeSlot[]>;
    timezone: string;
    // Canonical IANA (`America/New_York`); `timezone` may be an alias.
    clinic_timezone?: string;
    // Earliest date with a bookable slot; null when the whole searched range is booked.
    first_available_date?: string | null;
}

interface AvailableSlotsEnvelope {
    status: 'success' | 'error';
    data: AvailableSlotsData;
    message?: string;
}

export const AVAILABLE_SLOTS = 'available-slots';

const useAvailableSlots = (leadId: string | undefined, enabled: boolean) => {
    const req = useRequest();
    return useQuery<AvailableSlotsEnvelope>({
        queryKey: [AVAILABLE_SLOTS, leadId],
        queryFn: () =>
            req({
                url: `${envVars.API_RTM_HOSTNAME}scheduling/available-slots/`,
                method: 'GET',
                // useRequest also injects hero_organization_id into `query` and
                // builds the URL via buildUrl. Putting lead_id in `query` lets
                // both params end up on the same `?` instead of clashing with a
                // hard-coded querystring on the URL itself.
                query: { lead_id: leadId || '' }
            }),
        enabled: enabled && !!leadId,
        staleTime: 30_000
    });
};

export default useAvailableSlots;
